<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">视频任务</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.task_name" placeholder="请输入任务名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增任务
        </el-button>
      </div>

      <!-- 任务列表 -->
      <el-table :data="taskList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="task_name" label="任务名称" width="180" />
        <el-table-column label="账号" width="150">
          <template #default="{ row }">
            <span>{{ getGzhName(row.gzh_id) }}</span>
          </template>
        </el-table-column>

        <!-- 新增账号类型列 -->
        <el-table-column label="账号类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getPlatformTagType(getGzhFieldType(row.gzh_id))" size="small">
              {{ getPlatformText(getGzhFieldType(row.gzh_id)) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="视频分类" width="150">
          <template #default="{ row }">
            <span>{{ getCategoryName(row.video_id) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="视频总数" width="140" align="center">
          <template #default="{ row }">
            <el-tag type="info" size="small">{{ row.category_total_videos }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已使用视频" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="warning" size="small">{{ row.category_used_videos }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="今日已发送" width="120" align="center">
          <template #default="{ row }">
            <el-tag type="success" size="small">{{ row.today_tasks }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="每日发布数" width="120">
          <template #default="{ row }">
            <el-tag type="info">{{ row.daily_maximum_release }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="已发布总数" width="120">
          <template #default="{ row }">
            <el-tag type="warning">{{ row.total_tasks }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="总发布数" width="120">
          <template #default="{ row }">
            <el-tag type="success">{{ row.total_publications }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="错误信息" width="180">
          <template #default="{ row }">
            <el-tooltip v-if="row.error_message" :content="row.error_message" placement="top">
              <el-tag type="danger" size="small">错误</el-tag>
            </el-tooltip>
            <el-tag v-else type="info" size="small">无错误</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="最后发布时间" width="180">
          <template #default="{ row }">
            <span>{{ formatDateTime(row.last_execution_time) || '未发布' }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="目前状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-tag :type="getStatusTagType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="启动状态" width="120" fixed="right">
          <template #default="{ row }">
            <el-button :type="row.qd_status === 1 ? 'warning' : 'success'" size="small" @click="toggleTaskStatus(row)"
              :loading="row.statusLoading">
              {{ row.qd_status === 1 ? '暂停任务' : '启动任务' }}
            </el-button>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchTaskList" @current-change="fetchTaskList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="900px" :close-on-click-modal="false">
      <el-form ref="taskForm" :model="taskForm" label-width="140px" :rules="formRules">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="任务名称" prop="task_name">
              <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" size="large" />
            </el-form-item>

            <el-form-item label="账号" prop="gzh_id">
              <div class="account-select-container">
                <el-select v-model="taskForm.gzh_id" placeholder="请选择账号" size="large" multiple filterable
                  class="account-select">
                  <el-option v-for="gzh in gzhList" :key="gzh.id"
                    :label="`${gzh.ghz_name}(${getPlatformText(gzh.field_type)})`" :value="gzh.id">
                    <span>{{ gzh.ghz_name }}</span>
                    <span class="platform-tag" :style="{ backgroundColor: getPlatformBgColor(gzh.field_type) }">
                      {{ getPlatformText(gzh.field_type) }}
                    </span>
                  </el-option>
                </el-select>

                <div class="selection-actions">
                  <div class="quick-selection">
                    <span class="action-label">快速选择：</span>
                    <el-button-group class="platform-buttons">
                      <el-button size="small" @click="selectAllAccounts" class="select-all-btn">
                        <el-icon><Select /></el-icon>
                        全选
                      </el-button>
                      <el-button size="small" @click="clearAllAccounts" class="clear-all-btn">
                        <el-icon>
                          <Close />
                        </el-icon>
                        清空
                      </el-button>
                    </el-button-group>
                  </div>

                  <div class="platform-selection">
                    <span class="action-label">按平台选择：</span>
                    <div class="platform-tags">
                      <el-tag v-for="platform in platformTypes" :key="platform.value" :style="{
                        background: getPlatformBgColor(platform.value),
                        color: '#fff',
                        cursor: 'pointer',
                        margin: '2px',
                        border: 'none'
                      }" size="small" @click="selectPlatformAccounts(platform.value)">
                        {{ platform.label }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="作者名称">
              <el-input v-model="taskForm.gzh_author" placeholder="请输入作者名称" size="large" maxlength="8"
                show-word-limit />
            </el-form-item>

            <el-form-item label="标题分组" prop="title_id">
              <el-select v-model="taskForm.title_id" placeholder="请选择标题分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in titleList" :key="item.id" :label="item.title_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="描述分组" prop="desc_id">
              <el-select v-model="taskForm.desc_id" placeholder="请选择描述分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in descList" :key="item.id" :label="item.desc_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item label="话题分组" prop="topic_id">
              <el-select v-model="taskForm.topic_id" placeholder="请选择话题分组" size="large" style="width: 100%" clearable
                filterable>
                <el-option v-for="item in topicList" :key="item.id" :label="item.topic_name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="标题前缀">
              <el-input v-model="taskForm.add_prefix_to_title" placeholder="多个前缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="标题后缀">
              <el-input v-model="taskForm.title_with_suffix" placeholder="多个后缀用逗号分隔" size="large" />
            </el-form-item>

            <el-form-item label="视频分组" prop="video_id">
              <el-select v-model="taskForm.video_id" placeholder="请选择视频分组" size="large" style="width: 100%" filterable>
                <el-option v-for="item in videoList" :key="item.id" :label="item.video_name" :value="item.id" />
              </el-select>
            </el-form-item>

            <!-- 修改后的挂载选项部分 -->
            <el-form-item label="挂载类型">
              <el-radio-group v-model="mountingType" size="large" @change="handleMountingTypeChange">
                <el-radio-button label="shop">商品挂载</el-radio-button>
                <el-radio-button label="poi">地址挂载</el-radio-button>
                <el-radio-button label="mini">程序挂载</el-radio-button>
                <el-radio-button label="none">无需挂载</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="mountingType === 'shop'" label="商品挂载" prop="shop_mounting_id">
              <el-select v-model="taskForm.shop_mounting_id" placeholder="请选择商品挂载" size="large" style="width: 100%"
                filterable clearable>
                <el-option v-for="item in shopMountingList" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item v-if="mountingType === 'poi'" label="POI挂载" prop="poi_mounting_id">
              <el-select v-model="taskForm.poi_mounting_id" placeholder="请选择POI挂载" size="large" style="width: 100%"
                filterable clearable>
                <el-option v-for="item in poiMountingList" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>

            <el-form-item v-if="mountingType === 'mini'" label="小程序挂载" prop="mini_mounting_id">
              <el-select v-model="taskForm.mini_mounting_id" placeholder="请选择小程序挂载" size="large" style="width: 100%"
                filterable clearable>
                <el-option v-for="item in miniMountingList" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="总发布数" prop="total_publications">
              <el-input-number v-model="taskForm.total_publications" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="每日发布数" prop="daily_maximum_release">
              <el-input-number v-model="taskForm.daily_maximum_release" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="执行间隔(秒)" prop="fb_Interval">
              <el-input-number v-model="taskForm.fb_Interval" :min="10" :max="86400" size="large" style="width: 100%" />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="发布时间设置">
              <el-radio-group v-model="timeSettingType" size="large" @change="handleTimeSettingChange">
                <el-radio-button label="range">时间范围</el-radio-button>
                <el-radio-button label="specific">具体时间</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="开始时间" prop="api_release_time_start">
              <el-time-select v-model="taskForm.api_release_time_start" :max-time="taskForm.api_release_time_end"
                placeholder="开始时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="结束时间" prop="api_release_time_end">
              <el-time-select v-model="taskForm.api_release_time_end" :min-time="taskForm.api_release_time_start"
                placeholder="结束时间" start="00:00" step="01:00" end="24:00" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'specific'" label="具体时间">
              <el-select v-model="taskForm.every_hour" multiple filterable allow-create default-first-option
                placeholder="请输入时间并回车添加，如 09:30" size="large" style="width: 100%">
                <el-option v-for="time in defaultTimes" :key="time" :label="time" :value="time" />
              </el-select>
            </el-form-item>

            <el-form-item label="发布算法" prop="article_publishing_algorithm">
              <el-radio-group v-model="taskForm.article_publishing_algorithm" size="large">
                <el-radio :label="1">仅发未发过的视频</el-radio>
                <el-radio :label="2">重复发送</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="发布星期" prop="week_day">
              <el-checkbox-group v-model="taskForm.week_day" size="large">
                <el-checkbox-button v-for="day in weekDays" :key="day.value" :label="day.value">
                  {{ day.label }}
                </el-checkbox-button>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="备注信息">
          <el-input v-model="taskForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTaskForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete
} from '@element-plus/icons-vue'
import { Select, Close } from '@element-plus/icons-vue'

export default {
  name: 'VideoPublishDraft',
  components: { Search, Plus, Edit, Delete, Select, Close },
  data() {
    return {
      platformTypes: [  // 添加这个数组
        { label: '某音', value: 2 },
        { label: '某手', value: 3 },
        { label: '某微', value: 4 },
        { label: '某书', value: 5 },
        { label: '哔站', value: 6 },
        { label: '某TIK', value: 7 }
      ],

      loading: false,
      dialogVisible: false,
      dialogTitle: '新增任务',
      isEditMode: false,
      timeSettingType: 'range', // 'range' 或 'specific'
      mountingType: 'none', // 新增：挂载类型，可选'shop'、'poi'、'mini'、'none'
      searchForm: {
        task_name: ''
      },
      taskForm: {
        id: null,
        task_name: '',
        gzh_id: [], // 改为数组类型
        gzh_author: '',
        article_category_id: null,
        title_id: null,
        desc_id: null,
        topic_id: null,
        video_id: null,
        shop_mounting_id: null,
        poi_mounting_id: null,
        mini_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7], // 默认全选
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      },
      formRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        gzh_id: [
          {
            required: true,
            message: '请至少选择一个账号',
            trigger: 'change',
            type: 'array',
            validator: (rule, value, callback) => {
              // 编辑模式下允许单个值或数组
              if (this.isEditMode && (value === null || value === undefined || value === '')) {
                callback(new Error('请选择账号'));
              } else if (!this.isEditMode && (!Array.isArray(value) || value.length === 0)) {
                callback(new Error('请至少选择一个账号'));
              } else {
                callback();
              }
            }
          }
        ],
        video_id: [
          { required: true, message: '请选择视频分组', trigger: 'change' }
        ],
        total_publications: [
          { required: true, message: '请输入总发布数', trigger: 'blur' }
        ],
        daily_maximum_release: [
          { required: true, message: '请输入每日发布数', trigger: 'blur' }
        ],
        fb_Interval: [
          { required: true, message: '请输入执行间隔', trigger: 'blur' }
        ],
        api_release_time_start: [
          { required: true, message: '请选择开始时间', trigger: 'change' }
        ],
        api_release_time_end: [
          { required: true, message: '请选择结束时间', trigger: 'change' }
        ],
        article_publishing_algorithm: [
          { required: true, message: '请选择发布算法', trigger: 'change' }
        ],
        week_day: [
          { required: true, message: '请选择发布星期', trigger: 'change' }
        ]
      },
      taskList: [],
      gzhList: [],
      categoryList: [],
      titleList: [],
      descList: [],
      topicList: [],
      videoList: [],
      shopMountingList: [],
      poiMountingList: [],
      miniMountingList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      weekDays: [
        { label: '星期一', value: 1 },
        { label: '星期二', value: 2 },
        { label: '星期三', value: 3 },
        { label: '星期四', value: 4 },
        { label: '星期五', value: 5 },
        { label: '星期六', value: 6 },
        { label: '星期日', value: 7 }
      ],
      defaultTimes: [
        '08:00', '12:00', '18:00', '20:00', '22:00'
      ]
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchTaskList();
    this.fetchGzhList();
    this.fetchCategoryList();
    this.fetchTitleList();
    this.fetchDescList();
    this.fetchTopicList();
    this.fetchVideoList();
    this.fetchShopMountingList();
    this.fetchPoiMountingList();
    this.fetchMiniMountingList();
  },
  methods: {

    // 全选账号
    selectAllAccounts() {
      this.taskForm.gzh_id = this.gzhList.map(gzh => gzh.id);
    },

    // 清空账号选择
    clearAllAccounts() {
      this.taskForm.gzh_id = [];
    },
    // 选择特定平台的账号（切换选择状态）
    selectPlatformAccounts(platformType) {
      const platformAccountIds = this.gzhList
        .filter(gzh => gzh.field_type === platformType)
        .map(gzh => gzh.id);

      const currentSelected = this.taskForm.gzh_id || [];

      // 检查该平台是否已经全选
      const isAllSelected = platformAccountIds.every(id =>
        currentSelected.includes(id)
      );

      if (isAllSelected) {
        // 如果已全选，则取消选择该平台所有账号
        this.taskForm.gzh_id = currentSelected.filter(id =>
          !platformAccountIds.includes(id)
        );
      } else {
        // 如果未全选，则选择该平台所有账号（保留其他平台的选择）
        const otherPlatformAccounts = currentSelected.filter(id => {
          const gzh = this.gzhList.find(g => g.id === id);
          return gzh && gzh.field_type !== platformType;
        });

        this.taskForm.gzh_id = [...otherPlatformAccounts, ...platformAccountIds];
      }
    },
    // 获取平台背景颜色
    getPlatformBgColor(type) {
      const colorMap = {
        2: '#FF2C3C',    // 抖音：红色
        3: '#FF7800',    // 快手：橙色
        4: '#07C160',    // 视频号：绿色
        5: '#FF2442',    // 小红书：红色
        6: '#00A1D6',    // B站：蓝色
        7: '#000000'     // TikTok：黑色
      };
      return colorMap[type] || '#909399'; // 返回纯色而不是渐变
    },

    // 获取账号的平台类型
    getGzhFieldType(gzhId) {
      const gzh = this.gzhList.find(g => g.id == gzhId);
      return gzh ? gzh.field_type : null;
    },

    // 获取平台类型文本
    getPlatformText(type) {
      const platformMap = {
        2: '某音',
        3: '某手',
        4: '某微',
        5: '小红薯',
        6: '小B站',
        7: '海外某音'
      };
      return platformMap[type] || `平台${type}`;
    },

    // 获取平台标签颜色类型
    getPlatformTagType(type) {
      const typeMap = {
        2: 'danger',      // 抖音：红色
        3: 'primary',     // 快手：蓝色
        4: 'success',     // 视频号：绿色
        5: 'warning',     // 小红书：橙色
        6: 'info',        // B站：浅蓝色
        7: ''             // TikTok：默认
      };
      return typeMap[type] || 'info';
    },
    // 获取账号名称
    getGzhName(id) {
      const gzh = this.gzhList.find(item => item.id === id);
      return gzh ? gzh.ghz_name : '未知';
    },

    // 获取分类名称
    getCategoryName(id) {
      const category = this.videoList.find(item => item.id === id);
      return category ? category.video_name : '未知';
    },

    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待启动',
        2: '发布中',
        3: '发布完成',
        4: '发布失败'
      };
      return statusMap[status] || '未知状态';
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info',    // 待启动 - 蓝色
        2: 'warning', // 发布中 - 橙色
        3: 'success', // 发布完成 - 绿色
        4: 'danger'   // 发布失败 - 红色
      };
      return typeMap[status] || '';
    },

    // 切换任务状态
    async toggleTaskStatus(row) {
      try {
        row.statusLoading = true;
        const newQdStatus = row.qd_status === 1 ? 2 : 1;

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ks_video_task_management.php`,
          {
            action: 'update_status',
            store_id: this.userInfo.id,
            id: row.id,
            qd_status: newQdStatus
          }
        );

        if (data.success) {
          this.$message.success('状态更新成功');
          row.qd_status = newQdStatus;
          row.status = newQdStatus === 1 ? 2 : 1;
        } else {
          this.$message.error(data.error || '状态更新失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '操作失败';
        this.$message.error(`操作失败: ${errorMsg}`);
        console.error('切换状态错误:', error);
      } finally {
        row.statusLoading = false;
      }
    },

    // 获取任务列表
    async fetchTaskList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.task_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        if (this.searchForm.task_name) {
          params.task_name = this.searchForm.task_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ks_video_task_management.php`,
          params
        );

        if (data.data) {
          this.taskList = data.data.map(task => ({
            ...task,
            statusLoading: false,
            week_day: task.week_day ? JSON.parse(task.week_day) : [],
            every_hour: task.every_hour ? JSON.parse(task.every_hour) : []
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取任务列表失败');
        console.error('获取任务列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 获取账号列表
    async fetchGzhList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/gzh_auth_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.gzhList = data.data
        }
      } catch (error) {
        console.error('获取账号列表错误:', error);
      }
    },

    // 获取分类列表
    async fetchCategoryList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/article_category_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.categoryList = data.data;
        }
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    // 获取标题分组列表
    async fetchTitleList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/title_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.titleList = data.data;
        }
      } catch (error) {
        console.error('获取标题分组列表错误:', error);
      }
    },

    // 获取描述分组列表
    async fetchDescList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/desc_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.descList = data.data;
        }
      } catch (error) {
        console.error('获取描述分组列表错误:', error);
      }
    },

    // 获取话题分组列表
    async fetchTopicList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/topic_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.topicList = data.data;
        }
      } catch (error) {
        console.error('获取话题分组列表错误:', error);
      }
    },

    // 获取视频分组列表
    async fetchVideoList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/video_cp_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.videoList = data.data;
        }
      } catch (error) {
        console.error('获取视频分组列表错误:', error);
      }
    },

    // 获取商品挂载列表
    async fetchShopMountingList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/shop_mounting_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.shopMountingList = data.data;
        }
      } catch (error) {
        console.error('获取商品挂载列表错误:', error);
      }
    },

    // 获取POI挂载列表
    async fetchPoiMountingList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/poi_mounting_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.poiMountingList = data.data;
        }
      } catch (error) {
        console.error('获取POI挂载列表错误:', error);
      }
    },

    // 获取小程序挂载列表
    async fetchMiniMountingList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/mini_mounting_management.php`,
          {
            action: 'list_all',
            store_id: this.userInfo.id
          }
        );

        if (data.data) {
          this.miniMountingList = data.data;
        }
      } catch (error) {
        console.error('获取小程序挂载列表错误:', error);
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTaskList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增任务';
      this.isEditMode = false;
      this.timeSettingType = 'range';
      this.mountingType = 'none'; // 默认不挂载
      this.taskForm = {
        id: null,
        task_name: '',
        gzh_id: null,
        gzh_author: '',
        article_category_id: null,
        title_id: null,
        desc_id: null,
        topic_id: null,
        video_id: null,
        shop_mounting_id: null,
        poi_mounting_id: null,
        mini_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7],
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 处理挂载类型变化
    handleMountingTypeChange(type) {
      // 清空其他挂载类型的值
      if (type !== 'shop') this.taskForm.shop_mounting_id = null;
      if (type !== 'poi') this.taskForm.poi_mounting_id = null;
      if (type !== 'mini') this.taskForm.mini_mounting_id = null;

      // 如果选择"不挂载"，清空所有挂载选项
      if (type === 'none') {
        this.taskForm.shop_mounting_id = null;
        this.taskForm.poi_mounting_id = null;
        this.taskForm.mini_mounting_id = null;
      }
    },


    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑任务';
      this.isEditMode = true;

      // 判断是使用时间范围还是具体时间
      if (row.every_hour && row.every_hour.length > 0) {
        this.timeSettingType = 'specific';
      } else {
        this.timeSettingType = 'range';
      }
      // 设置挂载类型
      if (row.shop_mounting_id) {
        this.mountingType = 'shop';
      } else if (row.poi_mounting_id) {
        this.mountingType = 'poi';
      } else if (row.mini_mounting_id) {
        this.mountingType = 'mini';
      } else {
        this.mountingType = 'none';
      }
      this.taskForm = {
        ...row,
        gzh_id: row.gzh_id ? [row.gzh_id] : [], // 关键修改：将单个 ID 转换为数组
        api_release_time_start: this.formatTime(row.api_release_time_start),
        api_release_time_end: this.formatTime(row.api_release_time_end),
        week_day: row.week_day || [1, 2, 3, 4, 5, 6, 7],
        every_hour: row.every_hour || []
      };

      this.dialogVisible = true;
    },

    // 格式化时间 (HH:MM)
    formatTime(timeValue) {
      if (!timeValue) return '00:00';

      // 如果是字符串格式的数字
      if (typeof timeValue === 'string' && /^\d+$/.test(timeValue)) {
        const num = parseInt(timeValue);
        if (num === 24) return '24:00';
        return `${num.toString().padStart(2, '0')}:00`;
      }

      // 如果是数字类型
      if (typeof timeValue === 'number') {
        if (timeValue === 24) return '24:00';
        return `${timeValue.toString().padStart(2, '0')}:00`;
      }

      // 如果已经是时间格式就直接返回
      return timeValue;
    },


    // 处理时间设置类型变化
    handleTimeSettingChange(val) {
      if (val === 'range') {
        this.taskForm.every_hour = [];
      } else {
        this.taskForm.api_release_time_start = '00:00';
        this.taskForm.api_release_time_end = '24:00';
      }
    },

    // 提交表单（新增/编辑）
    submitTaskForm() {
      this.$refs.taskForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.taskForm,
              store_id: this.userInfo.id,
              week_day: JSON.stringify(this.taskForm.week_day)
            };

            // 编辑模式下只取第一个 gzh_id（因为编辑时只处理单个任务）
            if (this.isEditMode && Array.isArray(payload.gzh_id)) {
              payload.gzh_id = payload.gzh_id[0] || null;
            }

            // 处理时间设置
            if (this.timeSettingType === 'range') {
              payload.every_hour = '';
              // 转换时间格式为数字
              payload.api_release_time_start = parseInt(payload.api_release_time_start.split(':')[0]);
              // 转换结束时间（特殊处理24点）
              if (payload.api_release_time_end === '00:00') {
                payload.api_release_time_end = 24;
              } else {
                payload.api_release_time_end = parseInt(payload.api_release_time_end.split(':')[0]);
              }
            } else {
              // 具体时间模式
              payload.api_release_time_start = 0;
              payload.api_release_time_end = 0;
              payload.every_hour = JSON.stringify(payload.every_hour);
            }

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/ks_video_task_management.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTaskList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除任务
    async handleDelete(id) {
      this.$confirm('确定要删除该任务吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/ks_video_task_management.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTaskList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

:deep(.el-select-dropdown__item) {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.platform-tag {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.el-divider {
  margin: 20px 0;
}

/* 星期选择样式 */
:deep(.el-checkbox-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

:deep(.el-checkbox-button) {
  margin-right: 0;
}

:deep(.el-checkbox-button__inner) {
  border-left: 1px solid #dcdfe6;
  border-radius: 4px;
}

.account-select-container {
  width: 100%;
}

.account-select {
  width: 100%;
  margin-bottom: 12px;
}

.selection-actions {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  margin-top: 8px;
}

.quick-selection {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.platform-selection {
  display: flex;
  align-items: flex-start;
}

.action-label {
  font-size: 12px;
  color: #606266;
  margin-right: 10px;
  white-space: nowrap;
  line-height: 28px;
  flex-shrink: 0;
}

.platform-buttons {
  margin-right: 15px;
}

.select-all-btn {
  background: linear-gradient(135deg, #409eff, #337ecc);
  border-color: #409eff;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #66b1ff, #409eff);
    border-color: #66b1ff;
  }
}

.clear-all-btn {
  background: linear-gradient(135deg, #f56c6c, #dd6161);
  border-color: #f56c6c;
  color: white;

  &:hover {
    background: linear-gradient(135deg, #f78989, #f56c6c);
    border-color: #f78989;
  }
}


.platform-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

/* 平台标签样式需要穿透 */
:deep(.platform-tags .el-tag) {
  cursor: pointer;
  margin: 2px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}


.platform-tag {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: white;
  margin-left: 6px;
}

/* 下拉选项中的平台标签样式 */
:deep(.el-select-dropdown__item) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
}

:deep(.el-select-dropdown__item .platform-tag) {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: white;
  margin-left: 6px;
}

/* 平台标签颜色 */
:deep(.el-tag) {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}
</style>